import React from 'react';
import PropTypes from 'prop-types';
import { Tab } from 'react-bootstrap';
import numeral from 'numeral';
import { get } from 'lodash';

import moviesConfig from '../configs/movies-config';

import MovieAnticipation from './MovieAnticipation';
import MovieEngagement from './MovieEngagement';
import MovieInsights from './MovieInsights';
import { FilterTypes } from '../../components/Filters';
import BenchmarkBar from '../../components/BenchmarkBar';
import { buildMetricObj } from '../../utils/benchmark';

import './styles/MovieReport.css';
import MovieAffinity from './MovieAffinity';

export const renderAnticipationTab = (
  { anticipationQueries, queryActions, filters, blocked },
  movie,
  currentTab,
  benchmarkData,
  updateBenchmarkData,
  benchmark,
  displayBenchmarkTooltip,
  setDisplayBenchmarkTooltip
) => {
  const benchmarkMoviesCount = get(
    benchmarkData,
    'movieReportAnticipationBottom.movieCount',
    ''
  );

  const metricA = buildMetricObj(
    benchmarkData,
    'movieReportAnticipation',
    'followers',
    'Total Followers',
    'Measures total net users who are actively following a movie.',
    '0,0'
  );
  const metricB = buildMetricObj(
    benchmarkData,
    'movieReportAnticipation',
    'followerMovingAvg',
    'New Followers Last 7 Days',
    'Measures net new followers over the past 7 completed days.',
    '0,0'
  );
  const metricC = buildMetricObj(
    benchmarkData,
    'movieReportAnticipation',
    'age',
    'Age Group Affinity',
    'Represents the highest follower indexing age bucket relative to the general app population.'
  );
  const metricD = buildMetricObj(
    benchmarkData,
    'movieReportAnticipation',
    'gender',
    'Gender Affinity',
    'Represents the highest follower indexing gender bucket relative to the general app population.'
  );

  return (
    <Tab
      className="tvt-main__content-tab"
      eventKey={moviesConfig.MOVIE_ANTICIPATION_PAGE_ID}
      title="Anticipation"
    >
      {currentTab === moviesConfig.MOVIE_ANTICIPATION_PAGE_ID && (
        <>
          <BenchmarkBar
            benchmarkTitle="Similar Movies"
            contentType="movie"
            loading={benchmarkData.loadingTop || benchmarkData.loadingBottom}
            appliedBenchmark={benchmark}
            applyBenchmarkFilters={updateBenchmarkData}
            filters={filters}
            metricA={metricA}
            metricB={metricB}
            metricC={metricC}
            metricD={metricD}
            titlesCount={numeral(benchmarkMoviesCount).format('0,0')}
            displayBenchmarkTooltip={displayBenchmarkTooltip}
            setDisplayBenchmarkTooltip={setDisplayBenchmarkTooltip}
          />
          <section className="tvt-main__content">
            <MovieAnticipation
              queries={anticipationQueries}
              movie={movie}
              queryActions={queryActions}
              updateBenchmarkData={updateBenchmarkData}
              filters={filters}
              blocked={blocked}
            />
          </section>
        </>
      )}
    </Tab>
  );
};

export const renderAudienceTab = (
  { viewershipQueries, queryActions, filters, blocked },
  movie,
  currentTab,
  benchmarkData,
  updateBenchmarkData,
  benchmark,
  displayBenchmarkTooltip,
  setDisplayBenchmarkTooltip
) => {
  const benchmarkMoviesCount = get(
    benchmarkData,
    'movieReportViewershipBottom.movieCount',
    ''
  );

  const metricA = buildMetricObj(
    benchmarkData,
    'movieReportViewership',
    'followerConversion',
    'Follower Conversion Rate',
    'Measures the percent of movie followers who also viewed the movie.',
    '0.00%'
  );
  const metricB = buildMetricObj(
    benchmarkData,
    'movieReportViewership',
    'viewers',
    'Viewers',
    'Measures total unique users who have tracked the movie.',
    '0,0'
  );
  const metricC = buildMetricObj(
    benchmarkData,
    'movieReportViewership',
    'age',
    'Age Group Affinity',
    'Represents the highest viewer indexing age bucket relative to the general app population.'
  );
  const metricD = buildMetricObj(
    benchmarkData,
    'movieReportViewership',
    'gender',
    'Gender Affinity',
    'Represents the highest viewer indexing gender bucket relative to the general app population.'
  );

  return (
    <Tab
      className="tvt-main__content-tab"
      eventKey={moviesConfig.MOVIE_VIEWERSHIP_PAGE_ID}
      title="Audience"
    >
      {currentTab === moviesConfig.MOVIE_VIEWERSHIP_PAGE_ID && (
        <>
          <BenchmarkBar
            benchmarkTitle="Similar Movies"
            contentType="movie"
            loading={benchmarkData.loadingTop || benchmarkData.loadingBottom}
            appliedBenchmark={benchmark}
            applyBenchmarkFilters={updateBenchmarkData}
            filters={filters}
            metricA={metricA}
            metricB={metricB}
            metricC={metricC}
            metricD={metricD}
            titlesCount={numeral(benchmarkMoviesCount).format('0,0')}
            displayBenchmarkTooltip={displayBenchmarkTooltip}
            setDisplayBenchmarkTooltip={setDisplayBenchmarkTooltip}
          />
          <section className="tvt-main__content">
            <MovieInsights
              queries={viewershipQueries}
              movie={movie}
              updateBenchmarkData={updateBenchmarkData}
              queryActions={queryActions}
              filters={filters}
              blocked={blocked}
            />
          </section>
        </>
      )}
    </Tab>
  );
};

export const renderEngagementTab = (
  { engagementQueries, filters, queryActions, blocked },
  movie,
  currentTab,
  benchmarkData,
  updateBenchmarkData,
  benchmark,
  displayBenchmarkTooltip,
  setDisplayBenchmarkTooltip
) => {
  const benchmarkMoviesCount = get(
    benchmarkData,
    'movieReportEngagementBottom.movieCount',
    ''
  );

  const metricA = buildMetricObj(
    benchmarkData,
    'movieReportEngagement',
    'engagement',
    'Engagement Score',
    'Measures audience engagement with a piece of content beyond views. Score is calculated with weighted engagement interactions and normalized relatives to views.',
    '0.00'
  );
  const metricB = buildMetricObj(
    benchmarkData,
    'movieReportEngagement',
    'rating',
    'Audience Rating',
    'Measure the average audience rating for all user votes for a movie on a scale from 1 to 5. This is used to indicate overall quality or satisfaction with a piece of content.',
    '0.0'
  );

  const metricC = { hide: true };
  const metricD = { hide: true };
  return (
    <Tab
      className="tvt-main__content-tab"
      eventKey={moviesConfig.MOVIE_ENGAGEMENT_PAGE_ID}
      title="Engagement"
    >
      {currentTab === moviesConfig.MOVIE_ENGAGEMENT_PAGE_ID && (
        <>
          <section className="tvt-main__content">
            <BenchmarkBar
              benchmarkTitle="Similar Movies"
              contentType="movie"
              loading={benchmarkData.loadingTop || benchmarkData.loadingBottom}
              appliedBenchmark={benchmark}
              applyBenchmarkFilters={updateBenchmarkData}
              filters={filters}
              metricA={metricA}
              metricB={metricB}
              metricC={metricC}
              metricD={metricD}
              titlesCount={numeral(benchmarkMoviesCount).format('0,0')}
              displayBenchmarkTooltip={displayBenchmarkTooltip}
              setDisplayBenchmarkTooltip={setDisplayBenchmarkTooltip}
            />
            <MovieEngagement
              queries={engagementQueries}
              movie={movie}
              queryActions={queryActions}
              updateBenchmarkData={updateBenchmarkData}
              filters={filters}
              blocked={blocked}
            />
          </section>
        </>
      )}
    </Tab>
  );
};
export const renderAffinityTab = (
  { affinityQueries, filters, filtersActions, movie, queryActions, blocked },
  currentTab,
  isReleased
) => {
  return (
    <Tab
      className="tvt-main__content-tab"
      eventKey={moviesConfig.MOVIE_AFFINITY_PAGE_ID}
      title="Affinity"
    >
      {currentTab === moviesConfig.MOVIE_AFFINITY_PAGE_ID && (
        <>
          <section className="tvt-main__content">
            <MovieAffinity
              movie={movie}
              queryActions={queryActions}
              filtersActions={filtersActions}
              filters={filters}
              blocked={blocked}
              queries={affinityQueries}
              isReleased={isReleased}
            />
          </section>
        </>
      )}
    </Tab>
  );
};

renderAnticipationTab.propTypes = {
  anticipationLooks: PropTypes.shape({
    movieAnticipationFollowerAffinity: PropTypes.shape({}),
    movieAnticipationNewFollowers: PropTypes.shape({}),
    movieAnticipationTotalFollowers: PropTypes.shape({})
  }).isRequired,

  anticipationQueries: PropTypes.shape({
    movieFollowers: PropTypes.shape({})
  }).isRequired,

  movie: PropTypes.shape({}).isRequired,
  filters: PropTypes.shape({}).isRequired
};

renderAudienceTab.propTypes = {
  viewershipLooks: PropTypes.shape({
    movieAffinity: PropTypes.shape({}),
    movieAgeIndex: PropTypes.shape({}),
    movieCountryIndex: PropTypes.shape({}),
    movieGenderIndex: PropTypes.shape({}),
    movieViewerDropOff: PropTypes.shape({})
  }).isRequired,

  viewershipQueries: PropTypes.shape({
    movieFavorites: PropTypes.shape({}),
    movieFollowers: PropTypes.shape({}),
    movieMobility: PropTypes.shape({}),
    movieSampleSize: PropTypes.shape({}),
    movieScoreBinge: PropTypes.shape({}),
    movieScoreCompletion: PropTypes.shape({}),
    movieScoreEngagement: PropTypes.shape({}),
    movieScoreViewshare: PropTypes.shape({})
  }).isRequired,

  movie: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired
  }).isRequired
};

renderEngagementTab.propTypes = {
  engagementQueries: PropTypes.shape({
    characterVoteEpisodeRank: PropTypes.shape({}),
    hottestMoments: PropTypes.shape({}),
    memesList: PropTypes.shape({}),
    movieFollowers: PropTypes.shape({}),
    movieEmotionProfile: PropTypes.shape({}),
    movieEmotionIndexMeta: PropTypes.shape({}),
    commentFeed: PropTypes.shape({})
  }).isRequired,

  engagementLooks: PropTypes.shape({
    engagementRawTotals: PropTypes.shape({}),
    movieEmotionByEpisode: PropTypes.shape({})
  }).isRequired,
  fetchMemes: PropTypes.func.isRequired,
  fetchComments: PropTypes.func.isRequired,
  fetchMemesStatus: PropTypes.string.isRequired,

  movie: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired
  }).isRequired,

  filters: PropTypes.shape({
    fields: FilterTypes.FilterSetType, // eslint-disable-line react/no-typos
    groupings: FilterTypes.GroupingSetType // eslint-disable-line react/no-typos
  }).isRequired,

  filtersActions: PropTypes.shape({
    getFiltersForPage: PropTypes.func.isRequired,
    getFiltersForPageAsync: PropTypes.func.isRequired,
    resetFilters: PropTypes.func.isRequired
  }).isRequired
};

export default {
  renderEngagementTab,
  renderAudienceTab,
  renderAnticipationTab
};
